/**
 * Product Customer Badge
 *
 * Renders a product item's linked customer. Account-linked customers render as a
 * clickable chip (Buildings icon + name) that opens the account on the Accounts
 * board; legacy free-text/org customers fall back to a removable TagBadge.
 * Mirrors the task board's CustomerBadge.
 *
 * @module features/product-board/components/ProductCustomerBadge
 */

import { TagBadge, hashStringToTagColor } from '@chatsdet/ui';
import { Buildings, X } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useRemoveProductItemCustomer } from '../hooks/useProductItems';
import type { ProductItemCustomer } from '../types';

interface ProductCustomerBadgeProps {
  productItemId: string;
  customer: ProductItemCustomer;
  canEdit: boolean;
}

export function ProductCustomerBadge({
  productItemId,
  customer,
  canEdit,
}: ProductCustomerBadgeProps) {
  const navigate = useNavigate();
  const removeCustomer = useRemoveProductItemCustomer();

  if (customer.accountCardId) {
    const accountCardId = customer.accountCardId;
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2 pr-1 text-xs font-medium">
        <button
          type="button"
          onClick={() =>
            void navigate({ to: '/boards/accounts', search: { cardId: accountCardId } })
          }
          className="inline-flex items-center gap-1 transition-colors hover:text-primary"
        >
          <Buildings size={11} weight="bold" className="text-muted-foreground" />
          {customer.name}
        </button>
        {canEdit && (
          <button
            type="button"
            aria-label={`Remove ${customer.name}`}
            onClick={(e) => {
              e.stopPropagation();
              removeCustomer.mutate({ productItemId, customerId: customer.id });
            }}
            className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={10} weight="bold" />
          </button>
        )}
      </span>
    );
  }

  return (
    <TagBadge
      name={customer.name}
      color={hashStringToTagColor(customer.name)}
      size="sm"
      onRemove={
        canEdit
          ? () => removeCustomer.mutate({ productItemId, customerId: customer.id })
          : undefined
      }
    />
  );
}
